import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, Sliders, XLg } from 'react-bootstrap-icons';
import Pagination from '@mui/material/Pagination';
import CarCard from './CarCard';
import { CardSkeletons, EmptyBlock, ErrorBlock } from '../../common/ui/StateBlock';
import notify, { currentLanguage } from '../../common/ui/notify';
import language from '../../data/language';
import { isBlank } from '../../common/format';
import './Market.css';
import '../../common/ui/ui.css';

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 450;

// The filter keys that live in the URL. Keeping them in one list means the
// query string, the "clear filters" button and the request payload can never
// drift apart.
const FILTER_KEYS = [
    'keyword', 'mark', 'model', 'year_from', 'year_to', 'price_from', 'price_to',
    'millage_from', 'millage_to', 'fuel', 'transmission', 'category', 'drive',
    'location', 'status', 'sort', 'page'
];

const DEFAULTS = {sort: 'newest', status: '', page: '1'};

function parseFilters(search) {
    const params = new URLSearchParams(search);
    const filters = {};
    FILTER_KEYS.forEach((key) => {
        filters[key] = params.get(key) || DEFAULTS[key] || "";
    });
    return filters;
}

function buildSearch(filters) {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
        const value = filters[key];
        // Defaults and empty values stay out of the URL so it remains readable.
        if (isBlank(value)) return;
        if (DEFAULTS[key] !== undefined && String(value) === DEFAULTS[key]) return;
        params.set(key, String(value));
    });
    const search = params.toString();
    return search ? `?${search}` : "";
}

const Market = () => {
    const lang = currentLanguage();
    const strings = language[lang].market;
    const common = language[lang].common;

    const history = useHistory();
    const location = useLocation();

    // The URL is the single source of truth for the filter state, which makes
    // refresh, deep links and the browser back button work for free.
    const filters = useMemo(() => parseFilters(location.search), [location.search]);

    const [cars, setCars] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [facets, setFacets] = useState({marks: [], fuels: [], transmissions: [], categories: [], drives: [], locations: []});
    const [modelsByMark, setModelsByMark] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [keywordInput, setKeywordInput] = useState(filters.keyword);

    const requestRef = useRef(0);
    const gridRef = useRef(null);

    const applyFilters = useCallback((patch, {replace = false} = {}) => {
        const next = {...filters, ...patch};
        // Any change other than paging sends the user back to page one,
        // otherwise a narrower filter could land on an empty page 7.
        if (!Object.prototype.hasOwnProperty.call(patch, 'page')) next.page = '1';
        const search = buildSearch(next);
        if (search === location.search) return;
        if (replace) history.replace({pathname: location.pathname, search});
        else history.push({pathname: location.pathname, search});
    }, [filters, history, location.pathname, location.search]);

    // --- filter options -----------------------------------------------------
    useEffect(() => {
        let cancelled = false;
        axios.get('/api/marks')
            .then((res) => {
                if (cancelled) return;
                const pairs = Array.isArray(res.data.data) ? res.data.data : [];
                const grouped = pairs.reduce((acc, item) => {
                    if (!item || isBlank(item.mark)) return acc;
                    if (!acc[item.mark]) acc[item.mark] = [];
                    if (!isBlank(item.model) && !acc[item.mark].includes(item.model)) acc[item.mark].push(item.model);
                    return acc;
                }, {});
                setModelsByMark(grouped);
                setFacets((prev) => ({...prev, ...(res.data.facets || {})}));
            })
            .catch(() => {
                // Filter options are a convenience; the grid still works without
                // them, so this failure stays silent.
            });
        return () => { cancelled = true; };
    }, []);

    // --- results ------------------------------------------------------------
    useEffect(() => {
        const requestId = ++requestRef.current;
        const controller = new AbortController();

        setLoading(true);
        setFailed(false);

        const params = {limit: PAGE_SIZE};
        FILTER_KEYS.forEach((key) => {
            if (!isBlank(filters[key])) params[key] = filters[key];
        });

        axios.get('/api/cars', {params, signal: controller.signal})
            .then((res) => {
                // A slower earlier request must never overwrite a newer answer.
                if (requestId !== requestRef.current) return;
                setCars(Array.isArray(res.data.data) ? res.data.data : []);
                setTotal(Number(res.data.total) || 0);
                setTotalPages(Number(res.data.total_pages) || 0);
                setLoading(false);
            })
            .catch((error) => {
                if (axios.isCancel(error) || error.name === 'CanceledError') return;
                if (requestId !== requestRef.current) return;
                setCars([]);
                setTotal(0);
                setTotalPages(0);
                setFailed(true);
                setLoading(false);
                notify.apiError(error, common.error_message);
            });

        return () => controller.abort();
    }, [filters, common.error_message]);

    // Keep the search box in step when the URL changes from outside (back button).
    useEffect(() => {
        setKeywordInput(filters.keyword);
    }, [filters.keyword]);

    // Debounced search: typing must not fire a request per keystroke.
    useEffect(() => {
        if (keywordInput === filters.keyword) return undefined;
        const timer = setTimeout(() => {
            applyFilters({keyword: keywordInput}, {replace: true});
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keywordInput, filters.keyword, applyFilters]);

    useEffect(() => {
        window.scrollTo({top: 0, behavior: "smooth"});
    }, []);

    const years = useMemo(() => {
        const current = new Date().getFullYear();
        const list = [];
        for (let year = current; year >= 1990; year--) list.push(year);
        return list;
    }, []);

    const models = filters.mark ? (modelsByMark[filters.mark] || []) : [];

    const activeFilterCount = FILTER_KEYS
        .filter((key) => key !== 'page' && key !== 'sort')
        .filter((key) => !isBlank(filters[key])).length;

    const page = Math.max(1, parseInt(filters.page, 10) || 1);

    const goToPage = (nextPage) => {
        applyFilters({page: String(nextPage)});
        if (gridRef.current) {
            window.scrollTo({top: gridRef.current.offsetTop - 120, behavior: "smooth"});
        }
    };

    const clearFilters = () => {
        setKeywordInput("");
        history.push({pathname: location.pathname, search: ""});
    };

    const selectFilter = (key, label, options, {disabled = false} = {}) => (
        <div className="cx-field market-filter-field">
            <label className="cx-label" htmlFor={`filter-${key}`}>{label}</label>
            <select
                id={`filter-${key}`}
                className="cx-select"
                value={filters[key]}
                disabled={disabled}
                onChange={(e) => applyFilters({[key]: e.target.value})}
            >
                <option value="">{common.all}</option>
                {options.map((option) => {
                    const value = typeof option === 'object' ? option.value : option;
                    const text = typeof option === 'object' ? option.label : option;
                    return <option key={value} value={value}>{text}</option>;
                })}
            </select>
        </div>
    );

    const rangeFilter = (fromKey, toKey, fromLabel, toLabel, options) => (
        <>
            {selectFilter(fromKey, fromLabel, options)}
            {selectFilter(toKey, toLabel, options)}
        </>
    );

    const numberFilter = (key, label, placeholder) => (
        <div className="cx-field market-filter-field">
            <label className="cx-label" htmlFor={`filter-${key}`}>{label}</label>
            <input
                id={`filter-${key}`}
                className="cx-input"
                type="number"
                min="0"
                inputMode="numeric"
                placeholder={placeholder}
                defaultValue={filters[key]}
                key={`${key}-${filters[key]}`}
                onBlur={(e) => applyFilters({[key]: e.target.value})}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
        </div>
    );

    return (
        <div className="market-page">
            <div className="container">
                <header className="market-header">
                    <div className="market-header-text">
                        <h1 className="market-title">{strings.title}</h1>
                        <p className="market-subtitle">{strings.subtitle}</p>
                    </div>
                    <div className="market-header-count" aria-live="polite">
                        <strong>{total}</strong>
                        <span>{strings.results_unit}</span>
                    </div>
                </header>

                <div className="market-toolbar">
                    <div className="market-search">
                        <Search className="market-search-icon" aria-hidden="true" />
                        <input
                            type="search"
                            className="cx-input market-search-input"
                            placeholder={strings.search_placeholder}
                            value={keywordInput}
                            aria-label={strings.search_placeholder}
                            onChange={(e) => setKeywordInput(e.target.value)}
                        />
                    </div>

                    <div className="market-toolbar-actions">
                        <div className="market-sort">
                            <label className="cx-label" htmlFor="market-sort">{strings.sort}</label>
                            <select
                                id="market-sort"
                                className="cx-select"
                                value={filters.sort || 'newest'}
                                onChange={(e) => applyFilters({sort: e.target.value})}
                            >
                                <option value="newest">{strings.sort_newest}</option>
                                <option value="oldest">{strings.sort_oldest}</option>
                                <option value="price_asc">{strings.sort_price_asc}</option>
                                <option value="price_desc">{strings.sort_price_desc}</option>
                                <option value="millage_asc">{strings.sort_mileage_asc}</option>
                                <option value="millage_desc">{strings.sort_mileage_desc}</option>
                            </select>
                        </div>

                        <button
                            type="button"
                            className="cx-btn cx-btn-subtle market-filter-toggle"
                            aria-expanded={showFilters}
                            onClick={() => setShowFilters((prev) => !prev)}
                        >
                            <Sliders aria-hidden="true" />
                            {showFilters ? strings.hide_filters : strings.show_filters}
                            {activeFilterCount > 0 && <span className="market-filter-count">{activeFilterCount}</span>}
                        </button>
                    </div>
                </div>

                <section className={`market-filters ${showFilters ? "is-open" : ""}`} aria-label={strings.filters}>
                    {/* The inner wrapper carries no padding of its own, so the
                        collapsed panel can shrink all the way to zero height. */}
                    <div className="market-filters-inner">
                    <div className="market-filters-grid">
                        {selectFilter('mark', strings.brand, facets.marks || [])}
                        {selectFilter('model', strings.model, models, {disabled: !filters.mark})}
                        {rangeFilter('year_from', 'year_to', strings.year_from, strings.year_to, years)}
                        {numberFilter('price_from', strings.price_from, "0")}
                        {numberFilter('price_to', strings.price_to, "100000")}
                        {numberFilter('millage_from', strings.mileage_from, "0")}
                        {numberFilter('millage_to', strings.mileage_to, "300000")}
                        {selectFilter('fuel', strings.fuel, facets.fuels || [])}
                        {selectFilter('transmission', strings.transmission, facets.transmissions || [])}
                        {selectFilter('category', strings.body_type, facets.categories || [])}
                        {selectFilter('drive', strings.drive, facets.drives || [])}
                        {selectFilter('location', strings.location, facets.locations || [])}
                        <div className="cx-field market-filter-field">
                            <label className="cx-label" htmlFor="filter-status">{strings.status}</label>
                            <select
                                id="filter-status"
                                className="cx-select"
                                value={filters.status}
                                onChange={(e) => applyFilters({status: e.target.value})}
                            >
                                <option value="">{strings.status_all}</option>
                                <option value="for_sale">{strings.status_for_sale}</option>
                                <option value="sold">{strings.status_sold}</option>
                            </select>
                        </div>
                    </div>

                    {activeFilterCount > 0 && (
                        <div className="market-filters-footer">
                            <button type="button" className="cx-btn cx-btn-ghost cx-btn-sm" onClick={clearFilters}>
                                <XLg aria-hidden="true" /> {strings.clear_filters}
                            </button>
                        </div>
                    )}
                    </div>
                </section>

                <div className="market-grid" ref={gridRef}>
                    {loading && <CardSkeletons count={PAGE_SIZE > 6 ? 6 : PAGE_SIZE} className="market-grid-item" />}

                    {!loading && !failed && cars.map((car) => (
                        <div className="market-grid-item" key={car.id}>
                            <CarCard car={car} />
                        </div>
                    ))}
                </div>

                {!loading && failed && (
                    <ErrorBlock
                        title={common.error_title}
                        message={common.error_message}
                        retryLabel={common.retry}
                        onRetry={() => history.replace({pathname: location.pathname, search: location.search})}
                    />
                )}

                {!loading && !failed && cars.length === 0 && (
                    <EmptyBlock
                        title={strings.empty_title}
                        message={strings.empty_message}
                        action={activeFilterCount > 0 && (
                            <button type="button" className="cx-btn cx-btn-primary" onClick={clearFilters}>
                                {strings.clear_filters}
                            </button>
                        )}
                    />
                )}

                {!loading && !failed && totalPages > 1 && (
                    <nav className="market-pagination" aria-label={strings.page_of}>
                        <Pagination
                            count={totalPages}
                            page={Math.min(page, totalPages)}
                            onChange={(event, value) => goToPage(value)}
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            color="primary"
                            variant="outlined"
                            shape="rounded"
                        />
                    </nav>
                )}
            </div>
        </div>
    );
};

export default Market;
